import { ApolloError } from 'apollo-boost';
import { createSelector } from 'reselect';

import { RootState, ThunkAction } from '../store';
import { getApolloClient } from '../shared/graphql/client';
import { USER_QUERY } from 'queries/user/user.query';
import { GetUserQuery } from 'queries/user/user.query.generated';

// Constants
export const GET_USER_ATTEMPT = 'user/GET_USER_ATTEMPT';
export const GET_USER_SUCCESS = 'user/GET_USER_SUCCESS';
export const GET_USER_FAILURE = 'user/GET_USER_FAILURE';
export const SET_LOGGED_IN = 'user/SET_LOGGED_IN';

type Error = ApolloError;

export type UserState = {
    loading: boolean;
    error: Error[];
} & GetUserQuery['user'];
interface GetUserAttemptAction {
    type: typeof GET_USER_ATTEMPT;
}
interface GetUserSuccessAction {
    type: typeof GET_USER_SUCCESS;
    user: GetUserQuery['user'];
}
interface GetUserFailureAction {
    type: typeof GET_USER_FAILURE;
    error: Error;
}
interface SetLoggedInAction {
    type: typeof SET_LOGGED_IN;
    isLoggedIn: boolean;
}

export type UserActionTypes =
    | GetUserAttemptAction
    | GetUserSuccessAction
    | GetUserFailureAction
    | SetLoggedInAction;

// Actions
export const getUserAttempt = () => ({
    type: GET_USER_ATTEMPT
});

export const getUserSuccess = (user: GetUserQuery['user']) => ({
    type: GET_USER_SUCCESS,
    user
});

export const getUserFailure = (error: Error) => ({
    type: GET_USER_FAILURE,
    error
});

export const setLoggedIn = (isLoggedIn: boolean) => ({
    type: SET_LOGGED_IN,
    isLoggedIn
});

// Reducer
export const initialState = {
    loading: false,
    loggedIn: false,
    senderPropertyStatus: null,
    creationDate: null,
    userId: null,
    userUuid: null,
    error: []
};

const user = (state: UserState = initialState, action: UserActionTypes) => {
    switch (action.type) {
        case GET_USER_ATTEMPT:
            return {
                ...state,
                loading: true
            };
        case GET_USER_SUCCESS:
            return {
                ...state,
                loading: false,
                loggedIn: action.user?.loggedIn,
                senderPropertyStatus: action.user?.senderPropertyStatus,
                creationDate: action.user?.creationDate,
                userId: action.user?.userId,
                userUuid: action.user?.userUuid
            };
        case GET_USER_FAILURE:
            return {
                ...state,
                loading: false,
                error: [action.error]
            };
        case SET_LOGGED_IN:
            return {
                ...state,
                loggedIn: action.isLoggedIn
            };
        default:
            return state;
    }
};

// Action creators
export const getUser = (): ThunkAction => async (dispatch) => {
    dispatch(getUserAttempt());

    try {
        const {
            data: { user }
        } = await getApolloClient().query<GetUserQuery>({
            query: USER_QUERY
        });

        dispatch(getUserSuccess(user));
    } catch (error) {
        dispatch(getUserFailure(error));
    }
};

// Selectors
const getUserSelector = (state: RootState): UserState => state.user;
export const isLoggedInSelector = createSelector<RootState, UserState, boolean>(
    getUserSelector,
    (user) => !!user.loggedIn
);

export default user;
